import { supabase } from '../lib/supabase'

let onUnauthorized: (() => void) | null = null
let cachedAccessToken: string | null = null

const GET_SESSION_TIMEOUT_MS = 2000

export function setCachedAccessToken(token: string | null): void {
  cachedAccessToken = token
}

export function getCachedAccessToken(): string | null {
  return cachedAccessToken
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

export class ApiError extends Error {
  status: number
  code?: string
  conflictMonitorId?: string

  constructor(
    message: string,
    status: number,
    code?: string,
    conflictMonitorId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.conflictMonitorId = conflictMonitorId
  }
}

async function authHeaders(): Promise<HeadersInit> {
  if (!supabase) return {}

  if (cachedAccessToken) {
    return { Authorization: `Bearer ${cachedAccessToken}` }
  }

  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), GET_SESSION_TIMEOUT_MS)),
    ])
    if (!result) return {}
    const token = result.data.session?.access_token
    if (!token) return {}
    cachedAccessToken = token
    return { Authorization: `Bearer ${token}` }
  } catch {
    return {}
  }
}

export async function apiFetch(
  input: string,
  init?: RequestInit,
): Promise<Response> {
  const auth = await authHeaders()
  const response = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      ...auth,
      ...init?.headers,
    },
  })

  if (response.status === 401 && onUnauthorized) {
    onUnauthorized()
  }

  return response
}

function htmlApiError(status: number): ApiError {
  return new ApiError(
    'API returned HTML instead of JSON. If admin routes fail, ensure public/_redirects includes /api/admin and redeploy.',
    status,
  )
}

function messageFromErrorBody(parsed: Record<string, unknown>, rawText: string): string {
  if (typeof parsed.errorType === 'string' || typeof parsed.errorMessage === 'string') {
    return 'Something went wrong on our side. Please try again in a moment.'
  }
  const message =
    (typeof parsed.message === 'string' && parsed.message) ||
    (typeof parsed.error === 'string' && parsed.error)
  return message || rawText
}

function parseJsonText<T>(text: string, status: number): T {
  const trimmed = text.trimStart()
  if (trimmed.startsWith('<')) {
    throw htmlApiError(status)
  }
  if (!trimmed) {
    throw new ApiError(`Empty response (${status})`, status)
  }
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(`Invalid JSON response (${status})`, status)
  }
}

export async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!response.ok) {
    let message = `Request failed (${response.status})`
    let code: string | undefined
    let conflictMonitorId: string | undefined
    if (text.trimStart().startsWith('<')) {
      throw htmlApiError(response.status)
    }
    if (text) {
      try {
        const parsed = JSON.parse(text) as Record<string, unknown> & {
          error?: string
          code?: string
          message?: string
          conflictMonitorId?: string
        }
        message = messageFromErrorBody(parsed, text)
        code =
          typeof parsed.code === 'string'
            ? parsed.code
            : typeof parsed.error === 'string' && parsed.error.length < 64
              ? parsed.error
              : undefined
        conflictMonitorId =
          typeof parsed.conflictMonitorId === 'string'
            ? parsed.conflictMonitorId
            : undefined
      } catch {
        message = text
      }
    }
    throw new ApiError(message, response.status, code, conflictMonitorId)
  }
  return parseJsonText<T>(text, response.status)
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  return parseJson<T>(await apiFetch(path, init))
}
